import { DomainRedux } from "@/Shared/Types";
import { removePoller } from ".";

export interface PollerOptions {
    pollerKey : DomainRedux;
    frequency?: PollerFrequency;
    //minInterval?: number;
    //maxInterval?: number;
    initialInterval?: number;
}

export interface PollerActions {
    stop: () => void;
    start : () => void;
    restart : () => void;
    incrementSubscribers : () => void;
    decrementSubscribers : () => void;
    isRunning : () => boolean;
    readonly subscribers : number;
}

export const POLLER_FREQUENCY = {
  HIGH: {
    minInterval: 2_000,
    maxInterval: 16_000,
  },
  MID: {
    minInterval: 4_000,
    maxInterval: 32_000,
  },
  LOW: {
    minInterval: 6_000,
    maxInterval: 48_000,
  },
} as const;


export type PollerFrequency = typeof POLLER_FREQUENCY[keyof typeof POLLER_FREQUENCY];



/**
 * =====================================================
 * createPoller utility
 * =====================================================
 *
 * Provides a domain-agnostic polling mechanism with:
 * - dynamic interval adjustment based on fetched data,
 * - subscriber management to prevent redundant polling,
 * - lifecycle controls to start, stop, or restart polling.
 *
 * Core responsibilities:
 * - orchestrates repeated fetch operations,
 * - tracks the last successful polling timestamp,
 * - exposes a clean, action-driven API.
 *
 * Designed to be independent of any UI layer. Any system
 * component can interact through its controlled interface.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export function createPoller(
    fetchFn: (lastPollingDate: string) => Promise<any[]>,
    options : PollerOptions
) {
    
    /**
     * =====================================================
     * Internal state initialization
     * =====================================================
     *
     * Tracks current subscribers, polling interval, stop status,
     * last fetched timestamp, and active timeout.
     */
    let subscribers = 1;
    let interval = options.initialInterval ?? POLLER_FREQUENCY.HIGH.minInterval;
    const { minInterval, maxInterval } = initInterval();
    
    let stopped = true;
    let lastUpdate = new Date().toISOString();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

     /**
     * =====================================================
     * Interval initialization
     * =====================================================
     *
     * Ensures minInterval and maxInterval are properly set.
     * If user provides inconsistent values, corrects them:
     * - maxInterval can never be smaller than minInterval.
     */
    function initInterval() {
        let frequency = options.frequency ?? POLLER_FREQUENCY.HIGH;

        const minInterval = frequency.minInterval;
        const maxInterval = Math.max(minInterval, frequency.maxInterval); // plus lisible
        
       return {
          minInterval,
          maxInterval
       }
    }

    /**
     * =====================================================
     * Polling function
     * =====================================================
     *
     * Core loop executed at dynamic intervals.
     * Responsibilities:
     * - Stops if polling is currently stopped,
     * - Calls the provided fetch function with the last timestamp,
     * - Updates the last polling timestamp,
     * - Adjusts the interval:
     *   • resets to minInterval if new data is found,
     *   • doubles interval up to maxInterval if no data,
     * - Handles errors and continues polling,
     * - Schedules the next poll using setTimeout.
     */
    async function poll() {
        if (stopped) return;

        /**
         * const bufferSeconds = 1; // ou 1 selon la précision que tu veux
            lastUpdate = new Date(Date.now() - bufferSeconds * 1000).toISOString();
         */

        try {
            const result = await fetchFn(lastUpdate);
            
            lastUpdate = new Date().toISOString();

            if ((result ?? []).length > 0) {
                interval = minInterval;
                //console.log("✅ Found data, reset interval:", interval);
            } else {
                interval = Math.min(interval * 2, maxInterval);
                //console.log("⏳ No data, increase interval:", interval);
            }
        } catch (err) {
            console.error("❌ Polling error:", err);
            interval = Math.min(interval * 2, maxInterval);
        }

        timeoutId = setTimeout(poll, interval);
    }

    /**
     * =====================================================
     * Start polling
     * =====================================================
     *
     * Public method to begin polling.
     * Steps performed:
     * - Checks if polling is already running,
     * - Resets interval and last update timestamp,
     * - Initiates the poll loop.
     */
    function start() {
        if (!stopped) return; // déjà en cours
        stopped = false;
        interval = options.initialInterval ?? minInterval; // reset interval
        lastUpdate = new Date().toISOString(); // reset base time
        
        const delay = options.frequency?.maxInterval ?? POLLER_FREQUENCY.HIGH.maxInterval;
        console.debug(`Poller will start in ${delay} ms, lastUpdate set to ${lastUpdate}`);
        // Start after the first interval
        setTimeout(poll, delay);
    }

    /**
     * =====================================================
     * Stop polling
     * =====================================================
     *
     * Public method to halt polling.
     * Responsibilities:
     * - Sets stop flag,
     * - Clears any pending timeout to prevent future execution.
     */
    function stop() {
        stopped = true;
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    }

    /**
     * =====================================================
     * Restart polling
     * =====================================================
     *
     * Public method to reset the poller.
     * Steps:
     * - Stops current polling,
     * - Starts a new polling cycle with reset state.
     */
    function restart() {
        stop();
        start();
    }

    /**
     * =====================================================
     * Subscriber management
     * =====================================================
     *
     * Increases the count of active subscribers.
     */
    function incrementSubscribers() {
        subscribers++;
    }
    
    /**
     * =====================================================
     * Decrement Subscribers
     * =====================================================
     *
     * Reduces the count of active subscribers by one.
     *
     * Responsibilities:
     * - Ensures the subscriber count never goes below zero,
     * - Stops the poller when no subscribers remain,
     * - Removes the poller from the global registry to free resources.
     *
     * This ensures that polling only continues while there are
     * interested consumers and avoids unnecessary network calls.
     */
    function decrementSubscribers() {
        if (subscribers <= 0) return; // Guard: prevent negative subscriber count
        subscribers--;                 // Reduce active subscriber count

        // If no subscribers remain, stop polling and cleanup
        if (subscribers === 0) {
            stop();                                // Stop polling loop
            removePoller(options.pollerKey);      // Remove from global registry
        }
    }


    /**
     * =====================================================
     * Public API
     * =====================================================
     *
     * Exposes controlled poller actions:
     * - start, stop, restart,
     * - increment/decrement subscribers,
     * - check if currently running,
     * - readonly subscriber count.
     */
    return {
        start,
        stop,
        restart,
        isRunning: () => !stopped,
        incrementSubscribers,
        decrementSubscribers,
        get subscribers() {
            return subscribers;
        },
    } as PollerActions;
}
